import { defineStore } from 'pinia'
import {
  PublicStoreDepositsFormsCreate,
  PublicStoreDepositsFormsEdit,
} from '#components'
import { depositSchema, updateDepositSchema } from '~/schemas/deposits'
import type { Deposit } from '~/schemas/deposits'

export const useDepositsStore = defineStore('pos-deposits', () => {
  const log = useLogger('store:pos-deposits')
  const { get, post, put, delete: del } = useAuthenticatedFetch()

  // Get nuxtApp inside setup
  const nuxtApp = useNuxtApp()
  const { $dialog, $confirm } = nuxtApp
  const { t } = nuxtApp.$i18n

  // Get shift store with storeToRefs for reactivity
  const shiftStore = useShiftStore()
  const { businessId, shiftId } = storeToRefs(shiftStore)

  // Refs
  // Fund sources live on useExpenseStore — deposits reuse that state/fetch
  // rather than duplicating it here (there is no expense-type equivalent
  // catalog for deposits).
  const deposits: Ref<any[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const totalDeposits: Ref<number> = ref(0)
  const cashDeposits: Ref<number> = ref(0)

  // Methods
  function reset() {
    deposits.value = []
    loading.value = false
    totalDeposits.value = 0
    cashDeposits.value = 0
    log.info('✅ Deposits store reset to initial state')
  }

  function getDeposit(depositId: number): Deposit | null {
    return deposits.value.find((deposit: any) => deposit.id === depositId) ?? null
  }

  async function fetchDepositsByShift(shiftIdParam: number): Promise<void> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return
    }

    loading.value = true
    try {
      const res = await get(`/api/business/${businessId.value}/shift/${shiftIdParam}/deposits`)

      if (res?.success) {
        deposits.value = res?.data ?? []
        totalDeposits.value = res.total ?? 0
        cashDeposits.value = res.cashTotal ?? res.total ?? 0
        // Update shift store totals (cashDeposits drives the expected-cash calc)
        shiftStore.cashDeposits = res.cashTotal ?? res.total ?? 0
      }
    }
    catch {
      useErrorToast('Failed to load deposits')
    }
    finally {
      loading.value = false
    }
  }

  // Admin global-deposits page filters. Populates the same `deposits` array
  // used by getDeposit() so the Edit dialog can prefill from this table too.
  async function fetchDepositsGlobal(filters: { from?: string, to?: string, globalOnly?: boolean }): Promise<void> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return
    }

    loading.value = true
    try {
      const params = new URLSearchParams()
      if (filters.from) params.set('from', filters.from)
      if (filters.to) params.set('to', filters.to)
      if (filters.globalOnly) params.set('shift_id', 'global')

      const res = await get(`/api/business/${businessId.value}/deposits?${params.toString()}`)

      if (res?.success) {
        deposits.value = res?.data ?? []
        totalDeposits.value = res.total ?? 0
      }
    }
    catch {
      useErrorToast('Failed to load deposits')
    }
    finally {
      loading.value = false
    }
  }

  async function createDeposit(formData: Deposit): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    const data = {
      ...formData,
      business_id: businessId.value,
    }

    const validatedData = depositSchema.safeParse(data)

    if (!validatedData.success) {
      log.error('Validation failed:', validatedData.error)
      useErrorToast('Validation failed. Please check the entered details and try again.')
      return false
    }

    try {
      const res: { success: boolean } = await post(`/api/business/${businessId.value}/deposits`, validatedData.data)

      if (res.success) {
        useSuccessToast('Deposit created successfully')
        // Refresh deposits after creation when the deposit is shift-linked
        if (validatedData.data.shift_id) {
          await fetchDepositsByShift(Number(validatedData.data.shift_id))
        }
        return true
      }
      else {
        useErrorToast('Failed to create deposit')
        return false
      }
    }
    catch (error) {
      log.error('Failed to create deposit:', error)
      useErrorToast('Failed to create deposit')
      return false
    }
  }

  async function updateDeposit(depositId: number, formData: Deposit): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    const data = {
      ...formData,
      id: depositId,
      business_id: businessId.value,
    }

    const validatedData = updateDepositSchema.safeParse(data)

    if (!validatedData.success) {
      log.error('Validation failed:', validatedData.error)
      useErrorToast('Validation failed. Please check the entered details and try again.')
      return false
    }

    try {
      const res: { success: boolean } = await put(`/api/business/${businessId.value}/deposits/${depositId}`, validatedData.data)

      if (res.success) {
        useSuccessToast('Deposit updated successfully')
        // Refresh deposits after update when the deposit is shift-linked
        if (validatedData.data.shift_id) {
          await fetchDepositsByShift(Number(validatedData.data.shift_id))
        }
        return true
      }
      else {
        useErrorToast('Failed to update deposit')
        return false
      }
    }
    catch (error: any) {
      log.error('Failed to update deposit:', error)
      const errorMessage = error?.data?.message || t('Failed to update deposit')
      useErrorToast(errorMessage)
      return false
    }
  }

  async function deleteDeposit(depositId: number): Promise<void> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return
    }

    await del(`/api/business/${businessId.value}/deposits/${depositId}`)
    useSuccessToast('Deposit deleted')
  }

  const deleteDepositConfirm = (depositId: number, onSuccess?: () => void | Promise<void>): void => {
    $confirm.require({
      group: 'inline',
      message: t('Do you want to delete this record?'),
      icon: 'pi pi-info-circle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Delete'),
        severity: 'danger',
      },
      accept: async () => {
        try {
          await deleteDeposit(Number(depositId))
        }
        catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
          useErrorToast(errorMessage)
        }

        if (shiftId.value) {
          await fetchDepositsByShift(Number(shiftId.value))
        }
        await onSuccess?.()
      },
      reject: () => {
        useErrorToast('You have rejected')
      },
    })
  }

  // `onSuccess` lets callers (e.g. the admin global-deposits page) refresh
  // their own view; the shift-linked refetch above still runs independently.
  function createDepositModal(shiftIdParam: number | string | null, onSuccess?: () => void | Promise<void>) {
    $dialog.open(PublicStoreDepositsFormsCreate, {
      data: {
        shiftId: shiftIdParam ? Number(shiftIdParam) : null,
        businessId: businessId.value,
      },
      props: {
        header: t('New deposit'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        if (shiftIdParam) {
          await fetchDepositsByShift(Number(shiftIdParam))
        }
        await onSuccess?.()
      },
    })
  }

  function editDepositModal(depositId: number, shiftIdParam?: number | string | null, onSuccess?: () => void | Promise<void>) {
    $dialog.open(PublicStoreDepositsFormsEdit, {
      data: {
        depositId: Number(depositId),
        businessId: businessId.value,
      },
      props: {
        header: t('Edit deposit'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        const targetShiftId = shiftIdParam ?? shiftId.value
        if (targetShiftId) {
          await fetchDepositsByShift(Number(targetShiftId))
        }
        await onSuccess?.()
      },
    })
  }

  return {
    deposits,
    loading,
    totalDeposits,
    cashDeposits,
    fetchDepositsByShift,
    fetchDepositsGlobal,
    createDeposit,
    updateDeposit,
    getDeposit,
    deleteDeposit,
    deleteDepositConfirm,
    createDepositModal,
    editDepositModal,
    reset,
  }
}, {
  persist: {
    key: 'pos-deposit-store',
    storage: localStorage,
    paths: ['deposits'], // Only persist deposits data
  },
})
